import { getSidePanelLayersPlugins } from "plugins/plugin-registry"
import { useAppSelector } from "integrations/redux/hooks"
import { selectSearchArchivedLayersValue } from "./slice.new"
import { selectEntitiesFromIds } from "./slice"
import { useMemo } from "react"
import { sortArrayToDesiredOrder } from "tools/arrays/sort"

export type SidePanelArchivedLayersData = {
    id: string
    index: string
    archivedTimestamp: number
    plugin: ReturnType<typeof getSidePanelLayersPlugins>[number]
}

const useArchivedLayersData = () => {
    const sidePanelPlugins = getSidePanelLayersPlugins()
    const data = sidePanelPlugins.flatMap((plugin) => {
        const entities = plugin.useSidePanelEntities()
        return entities
            .filter((entity) => !!entity.archivedTimestamp)
            .map((entity) => ({
                ...entity,
                plugin,
            })) as SidePanelArchivedLayersData[]
    })
    return data
}

export const useArchivedLayerCount = () => {
    const archivedLayersData = useArchivedLayersData()
    return archivedLayersData.length
}

const useArchivedLayersEntityMap = () => {
    const archivedLayersData = useArchivedLayersData()
    const entities = useAppSelector(
        selectEntitiesFromIds(archivedLayersData.map((layer) => layer.id))
    )
    const entityMap = useMemo(() => {
        return Object.fromEntries(entities.map((entity) => [entity.id, entity]))
    }, [entities])
    return entityMap
}

export const useFilteredArchivedLayersData = () => {
    const sidePanelData = useArchivedLayersData()
    const entityMap = useArchivedLayersEntityMap()
    const searchArchivedLayersValue = useAppSelector(selectSearchArchivedLayersValue)
    const filterList = (list: SidePanelArchivedLayersData[]) => {
        return list
            .map((data): SidePanelArchivedLayersData | null => {
                const entity = entityMap[data.id]
                const matchesSearch = entity.name
                    .toLowerCase()
                    .includes(searchArchivedLayersValue.toLocaleLowerCase())
                if (matchesSearch) {
                    return data
                }
                return null
            })
            .filter((x): x is SidePanelArchivedLayersData => x !== null)
    }
    const filteredList = filterList(sidePanelData)
    filteredList.sort((a, b) => b.archivedTimestamp - a.archivedTimestamp)
    return filteredList
}

const archivedPluginOrder = [
    "Orthomosaic",
    "Hotspots",
    "Fire Perimeter",
    "Colorfinder",
    "Live Stream",
    "Video",
    "Panorama",
    "Point",
    "Line",
    "Text",
    "Ruler",
    "Geo-Referenced PDF",
] as const

export const useArchivedLayersCategories = () => {
    const archivedLayersData = useArchivedLayersData()
    sortArrayToDesiredOrder({
        data: archivedLayersData,
        getSortingValue: (t) => t.plugin.categoryName,
        desiredOrder: archivedPluginOrder,
    })
    const data = archivedLayersData.reduce(
        (acc, cur) => {
            if (!acc[cur.plugin.categoryName]) {
                acc[cur.plugin.categoryName] = []
            }
            acc[cur.plugin.categoryName].push(cur.id)
            return acc
        },
        {} as Record<string, string[]>
    )
    return data
}
